import { httpService } from "@/app/services/common/httpService";
import { urlManager } from "@/app/utils/url-manager";
import type { ApiResponse } from "@/app/services/common/httpService";
import { TraineeDetailsFormValues } from "./TraineeDetailsFormModal.types";
import {
  assertTraineeDetailsFormValues,
  isNamedChannelRowComplete,
} from "./trainee-details-form.schema";

export interface CreateWarriorProfileRequest {
  name: string;
  email: string;
  whatsappNumber: string;
  dateOfBirth?: string;
  profession?: string;
  experienceYears?: string;
  education?: string;
  location?: string;
  ishaPrograms?: string;
  channels?: CreateWarriorChannelRequest[];
  channelGrowthJourney?: string;
  channelsMonetized?: boolean;
  monetizedChannelsDetails?: string;
  teamNotes?: string;
}

export interface CreateWarriorChannelRequest {
  platform: string;
  channelName: string;
  link: string;
  language?: string;
  monetized?: boolean;
  audiencePersona?: string;
  channelGenre?: string;
  videoFormat?: string;
  strengths?: string;
  postingRegularly?: string;
  postingChallenge?: string;
  contentCreationChallenges?: string;
  channelGrowthJourney?: string;
  monetizedChannelsDetails?: string;
  teamNotes?: string;
}

export interface WarriorProfileResponse {
  id: string;
  name: string;
  email: string;
  whatsappNumber: string;
  dateOfBirth?: string | Date | null;
  profession?: string;
  experienceYears?: string;
  education?: string;
  location?: string;
  ishaPrograms?: string;
  channels?: WarriorChannelResponse[];
  channelGrowthJourney?: string;
  channelsMonetized?: boolean;
  monetizedChannelsDetails?: string;
  teamNotes?: string;
  createdAt: string;
  updatedAt: string;
}

export interface WarriorChannelResponse {
  id: string;
  platform: string;
  channelName: string;
  link: string;
  language?: string;
  monetized: boolean;
  audiencePersona?: string;
  channelGenre?: string;
  videoFormat?: string;
  strengths?: string;
  postingRegularly?: string;
  postingChallenge?: string;
   contentCreationChallenges?: string;
  channelGrowthJourney?: string;
  monetizedChannelsDetails?: string;
  teamNotes?: string;
}

/**
 * Maps form values to the API request format
 */
function mapFormValuesToRequest(
  values: TraineeDetailsFormValues
): CreateWarriorProfileRequest {
  const channels: CreateWarriorChannelRequest[] = [];

  const toOptional = (value: string | undefined): string | undefined => {
    return value && value.trim() ? value.trim() : undefined;
  };

  const hasChannelIdentity = (
    channelName?: string,
    link?: string,
    language?: string
  ): boolean => isNamedChannelRowComplete({ channelName, link, language });

  const hasOtherChannelIdentity = (
    channelName?: string,
    link?: string
  ): boolean => {
    return !!(channelName?.trim() || link?.trim());
  };

  const normalizedPostingRegularly = values.postingRegularly
    ? values.postingRegularly
    : undefined;
  const channelGrowthJourney = toOptional(values.channelGrowthJourney);
  const monetizedFlag =
    values.channelsMonetized === undefined
      ? undefined
      : values.channelsMonetized === "Yes";
  const monetizedChannelsDetails = toOptional(
    values.monetizedChannelsDetails
  );
  const teamNotes = toOptional(values.teamNotes);

  // Shared overview fields for the main channel
  const audiencePersona = toOptional(values.audiencePersona);
  const channelGenre = toOptional(values.channelGenre);
  const videoFormat = toOptional(values.videoFormat);
  const strengths = toOptional(values.strengths);
  const contentCreationChallenges = toOptional(
    values.contentCreationChallenges
  );

  const addNamedChannel = (
    platform: string,
    channelName?: string,
    link?: string,
    language?: string | undefined,
    isMainChannel: boolean = false
  ): boolean => {
    const trimmedName = channelName?.trim() ?? "";
    const trimmedLink = link?.trim() ?? "";
    const trimmedLanguage = language?.trim() ?? "";

    if (!trimmedName || !trimmedLink || !trimmedLanguage) {
      return false;
    }

    channels.push({
      platform,
      channelName: trimmedName,
      link: trimmedLink,
      language: trimmedLanguage,
      postingRegularly: isMainChannel ? normalizedPostingRegularly : undefined,
      postingChallenge: isMainChannel
        ? toOptional(values.postingChallenge)
        : undefined,
      audiencePersona: isMainChannel ? audiencePersona : undefined,
      channelGenre: isMainChannel ? channelGenre : undefined,
      videoFormat: isMainChannel ? videoFormat : undefined,
      strengths: isMainChannel ? strengths : undefined,
      contentCreationChallenges: isMainChannel
        ? contentCreationChallenges
        : undefined,
      channelGrowthJourney: isMainChannel ? channelGrowthJourney : undefined,
      monetized: monetizedFlag ?? false,
      monetizedChannelsDetails,
      teamNotes,
    });

    return true;
  };

  const addOtherChannel = (
    platform: string,
    channelName?: string,
    link?: string,
    isMainChannel: boolean = false
  ): boolean => {
    const trimmedName = channelName?.trim() ?? "";
    const trimmedLink = link?.trim() ?? "";

    if (!trimmedName && !trimmedLink) {
      return false;
    }

    channels.push({
      platform,
      channelName: trimmedName,
      link: trimmedLink,
      language: undefined,
      postingRegularly: isMainChannel ? normalizedPostingRegularly : undefined,
      postingChallenge: isMainChannel
        ? toOptional(values.postingChallenge)
        : undefined,
      audiencePersona: isMainChannel ? audiencePersona : undefined,
      channelGenre: isMainChannel ? channelGenre : undefined,
      videoFormat: isMainChannel ? videoFormat : undefined,
      strengths: isMainChannel ? strengths : undefined,
      contentCreationChallenges: isMainChannel
        ? contentCreationChallenges
        : undefined,
      channelGrowthJourney: isMainChannel ? channelGrowthJourney : undefined,
      monetized: monetizedFlag ?? false,
      monetizedChannelsDetails,
      teamNotes,
    });

    return true;
  };

  const youtubeChannels = values.youtubeChannels ?? [];
  const instagramChannels = values.instagramChannels ?? [];
  const otherChannels = values.otherChannels ?? [];

  // Determine main channel priority: YouTube -> Instagram -> other
  // Pick the first *non-empty* channel row so Section 4 attaches to something that will be saved.
  const mainYoutube = youtubeChannels.find((ch) =>
    hasChannelIdentity(ch.channelName, ch.link, ch.language)
  );
  const mainInstagram = instagramChannels.find((ch) =>
    hasChannelIdentity(ch.channelName, ch.link, ch.language)
  );
  const mainOther = otherChannels.find((ch) =>
    hasOtherChannelIdentity(ch.name, ch.link)
  );

  let mainAssigned = false;

  // Add YouTube channels
  youtubeChannels.forEach((yt, index) => {
    const isMain =
      !mainAssigned &&
      ((mainYoutube && yt === mainYoutube) ||
        (!mainYoutube && !mainInstagram && !mainOther && index === 0));
    const added = addNamedChannel(
      "YouTube",
      yt.channelName,
      yt.link,
      yt.language,
      isMain
    );
    if (isMain && added) mainAssigned = true;
  });

  // Add Instagram channels
  instagramChannels.forEach((ig, index) => {
    const isMain =
      !mainAssigned &&
      ((mainInstagram && ig === mainInstagram) ||
        (!mainYoutube && !mainInstagram && mainOther && ig === mainInstagram) ||
        (!mainYoutube && !mainInstagram && !mainOther && index === 0));
    const added = addNamedChannel(
      "Instagram",
      ig.channelName,
      ig.link,
      ig.language,
      isMain
    );
    if (isMain && added) mainAssigned = true;
  });

  // Add other channels
  otherChannels.forEach((channel, index) => {
    const isMain =
      !mainAssigned &&
      ((mainOther && channel === mainOther) ||
        (!mainYoutube && !mainInstagram && !mainOther && index === 0));
    const added = addOtherChannel(
      channel.platform?.trim() || "Other",
      channel.name,
      channel.link,
      isMain
    );
    if (isMain && added) mainAssigned = true;
  });

  return {
    name: values.name.trim(),
    email: values.email.trim(),
    whatsappNumber: values.whatsappNumber.trim(),
    dateOfBirth: toOptional(values.dateOfBirth),
    profession: toOptional(values.profession),
    experienceYears: toOptional(values.experienceYears),
    education: toOptional(values.education),
    location: toOptional(values.location),
    ishaPrograms: toOptional(values.ishaPrograms),
    channels: channels.length > 0 ? channels : undefined,
  };
}

/**
 * Maps a warrior profile API response into the modal form shape
 */
export function mapProfileResponseToFormValues(
  profile: WarriorProfileResponse | undefined
): Partial<TraineeDetailsFormValues> | undefined {
  if (!profile) {
    return undefined;
  }

  const normalizeDate = (value?: string | Date | null): string => {
    if (!value) return "";
    if (value instanceof Date) {
      return value.toISOString().slice(0, 10);
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return typeof value === "string" ? value : "";
    }
    return date.toISOString().slice(0, 10);
  };

  const youtubeChannels =
    profile.channels?.filter((channel) => channel.platform === "YouTube") ?? [];
  const instagramChannels =
    profile.channels?.filter(
      (channel) => channel.platform === "Instagram"
    ) ?? [];
  const youtubeChannel = youtubeChannels[0];
  const instagramChannel = instagramChannels[0];
  const channelMetaSource =
    profile.channels?.find(
      (channel) =>
        channel.channelGrowthJourney ||
        channel.teamNotes ||
        channel.monetized ||
        channel.monetizedChannelsDetails
    ) || profile.channels?.[0];
  const hasChannels = (profile.channels?.length ?? 0) > 0;
  const hasMonetizedChannel = profile.channels?.some(
    (channel) => channel.monetized
  );
  const otherChannelsRaw =
    profile.channels
      ?.filter(
        (channel) =>
          channel.platform !== "YouTube" && channel.platform !== "Instagram"
      ) ?? [];

  const mainChannel =
    youtubeChannel || instagramChannel || otherChannelsRaw[0];

  // Prefer the channel that actually contains Section 4 fields, regardless of array order.
  const section4Source =
    profile.channels?.find(
      (channel) =>
        channel.audiencePersona ||
        channel.channelGenre ||
        channel.videoFormat ||
        channel.strengths ||
        channel.contentCreationChallenges ||
        channel.postingRegularly ||
        channel.postingChallenge
    ) || mainChannel || profile.channels?.[0];

  const channelInsightSource =
    profile.channels?.find(
      (channel) =>
        channel.channelGrowthJourney ||
        channel.teamNotes ||
        channel.monetized ||
        channel.monetizedChannelsDetails
    ) || mainChannel || profile.channels?.[0];

  const postingRegularly =
    section4Source?.postingRegularly &&
    ["Yes", "No", "Sometimes"].includes(section4Source.postingRegularly)
      ? (section4Source.postingRegularly as "Yes" | "No" | "Sometimes")
      : undefined;

  return {
    name: profile.name ?? "",
    email: profile.email ?? "",
    whatsappNumber: profile.whatsappNumber ?? "",
    dateOfBirth: normalizeDate(profile.dateOfBirth),
    profession: profile.profession ?? "",
    experienceYears: profile.experienceYears ?? "",
    education: profile.education ?? "",
    location: profile.location ?? "",
    ishaPrograms: profile.ishaPrograms ?? "",

    youtubeChannels: youtubeChannels.map((ch) => ({
      channelName: ch.channelName ?? "",
      link: ch.link ?? "",
      language: ch.language ?? "",
    })),

    instagramChannels: instagramChannels.map((ch) => ({
      channelName: ch.channelName ?? "",
      link: ch.link ?? "",
      language: ch.language ?? "",
    })),
    postingRegularly,
    postingChallenge: section4Source?.postingChallenge ?? "",
    audiencePersona: section4Source?.audiencePersona ?? "",
    channelGenre: section4Source?.channelGenre ?? "",
    videoFormat: section4Source?.videoFormat ?? "",
    strengths: section4Source?.strengths ?? "",
    contentCreationChallenges: section4Source?.contentCreationChallenges ?? "",
    channelGrowthJourney: channelInsightSource?.channelGrowthJourney ?? "",
    channelsMonetized: !hasChannels
      ? undefined
      : hasMonetizedChannel
      ? "Yes"
      : "No",
    monetizedChannelsDetails:
      channelInsightSource?.monetizedChannelsDetails ?? "",
    teamNotes: channelInsightSource?.teamNotes ?? "",
    otherChannels: otherChannelsRaw.map((channel) => ({
      name: channel.channelName ?? "",
      link: channel.link ?? "",
      platform: channel.platform ?? "Other",
    })),
  };
}

/**
 * Create a new warrior profile
 */
export async function createWarriorProfile(
  values: TraineeDetailsFormValues,
  userId: string
): Promise<WarriorProfileResponse> {
  assertTraineeDetailsFormValues(values);
  const requestData = mapFormValuesToRequest(values);
  const url = urlManager.api.warriorProfiles.create(userId);

  try {
    const response = await httpService.post<
      ApiResponse<WarriorProfileResponse>
    >(url, requestData);

    // Handle different response structures
    if (response && typeof response === "object") {
      if ("entity" in response && response.entity) {
        return response.entity as WarriorProfileResponse;
      }

      if ("data" in response && response.data) {
        return response.data as WarriorProfileResponse;
      }

      const possibleEntity = response as unknown;
      if (
        possibleEntity &&
        typeof possibleEntity === "object" &&
        "id" in possibleEntity
      ) {
        return possibleEntity as WarriorProfileResponse;
      }
    }

    throw new Error("Invalid response structure");
  } catch (error: any) {
    // Re-throw with a more user-friendly message
    if (error?.response?.status === 409) {
      throw new Error("A warrior profile with this email already exists");
    }
    if (error?.response?.status === 400) {
      const serverMessage = error?.response?.data?.message;
      const formattedMessage = Array.isArray(serverMessage)
        ? serverMessage.join(", ")
        : typeof serverMessage === "string"
        ? serverMessage
        : "Invalid data provided";
      throw new Error(formattedMessage);
    }
    throw error;
  }
}

export async function updateWarriorProfile(
  id: string,
  values: TraineeDetailsFormValues
): Promise<WarriorProfileResponse> {
  assertTraineeDetailsFormValues(values);
  const requestData = mapFormValuesToRequest(values);
  const url = urlManager.api.warriorProfiles.update(id);

  const response = await httpService.patch<ApiResponse<WarriorProfileResponse>>(
    url,
    requestData
  );
  return response?.entity || response;
}
